import { useEffect, useRef, useState } from "react";
import { createFileRoute, retainSearchParams, useNavigate } from "@tanstack/react-router";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { authGuardWithPermissions } from "@/utils/authGuard.ts";
import type { RouterContextType } from "@/api/permissions/permissions.api.ts";
import { ProposalGenerationForm } from "@/components/domain/WorkOrder/ProposalGenerationForm/ProposalGenerationForm";
import type { ProposalFormData } from "@/components/domain/WorkOrder/ProposalGenerationForm/ProposalGenerationForm.types";
import {
  createDispatcherFilledAssessment,
  triggerProposalGeneration,
} from "@/api/work-orders/proposal/proposal.mutations";
import { ProposalQueryKeys } from "@/api/work-orders/proposal/proposal.queries.types";
import { useQueryClient } from "@tanstack/react-query";
import { useProposalDraftStore } from "@/store/proposalDraftStore";
import type { ProposalDraft } from "@/store/proposalDraftStore";
import { formatDateTime } from "@/utils/dateFormatter";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ModalActions,
  Button,
  ModalContent,
} from "@onengine/component-library";
import { useSessionStore } from "@/store/sessionStore/sessionStore.ts";

export const Route = createFileRoute("/_authenticated/work-orders/$workOrderId/proposal/create/")({
  component: RouteComponent,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { workOrderId } = Route.useParams();
  const queryClient = useQueryClient();

  const userId = useSessionStore((s) => s.userId) ?? "";
  const { getDraft, deleteDraft } = useProposalDraftStore();

  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [capturedDraft, setCapturedDraft] = useState<ProposalDraft | null>(null);
  const [activeDraft, setActiveDraft] = useState<ProposalDraft | null>(null);
  const [formKey, setFormKey] = useState(0);
  const draftChecked = useRef(false);

  useEffect(() => {
    if (!userId || draftChecked.current) return;
    draftChecked.current = true;

    const draft = getDraft(userId, workOrderId);
    if (draft) {
      setCapturedDraft(draft);
      setDraftModalOpen(true);
    }
  }, [userId, workOrderId, getDraft]);

  const handleResumeDraft = () => {
    setActiveDraft(capturedDraft);
    setFormKey((k) => k + 1);
    setDraftModalOpen(false);
  };

  const handleStartFresh = () => {
    if (userId) deleteDraft(userId, workOrderId);
    setDraftModalOpen(false);
  };

  const handleCancel = () => {
    navigate({ to: "/work-orders/$workOrderId", params: { workOrderId } });
  };

  const handleSubmit = async (data: ProposalFormData) => {
    const { assessmentId } = await createDispatcherFilledAssessment({
      workOrderId,
      source: "dispatcher-filled",
      data,
    });

    const { proposalId } = await triggerProposalGeneration(assessmentId);

    if (userId) deleteDraft(userId, workOrderId);

    queryClient.invalidateQueries({ queryKey: [ProposalQueryKeys.list, workOrderId] });

    // Temporary comment
    // Navigate to Proposal Content view once that route exists.
    // Replace with: navigate({ to: "/work-orders/$workOrderId/proposal/$proposalId", params: { workOrderId, proposalId } });
    void proposalId;
    navigate({ to: "/work-orders/$workOrderId", params: { workOrderId } });
  };

  return (
    <>
      <Modal open={draftModalOpen} onClose={handleStartFresh} closeOnClickOutside={false}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Resume draft?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>
              You have a saved draft for this work order from{" "}
              {formatDateTime(capturedDraft?.savedAt)}. Uploaded images are not restored from
              drafts.
            </p>
          </ModalBody>
          <ModalFooter>
            <ModalActions justify="center" position={"right"}>
              <Button variant="neutral-secondary" size="large" onClick={handleStartFresh}>
                Start Fresh
              </Button>
              <Button variant="primary" size="large" onClick={handleResumeDraft}>
                Resume Draft
              </Button>
            </ModalActions>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ProposalGenerationForm
        key={formKey}
        workOrderId={workOrderId}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        initialDraft={activeDraft}
      />
    </>
  );
}
